import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Checkbox
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import InventoryIcon from '@mui/icons-material/Inventory'
import MedicationIcon from '@mui/icons-material/Medication'
import CategoryIcon from '@mui/icons-material/Category'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { dcApi } from '../services/dc.api'
import { DCInventoryItem } from '../types/dc.types'

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-'
  return `Rs. ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const isExpired = (expiryDate: string | null | undefined): boolean => {
  if (!expiryDate) return false
  return new Date(expiryDate) < new Date()
}

const isExpiringIn30Days = (expiryDate: string | null | undefined): boolean => {
  if (!expiryDate) return false
  const expiry = new Date(expiryDate)
  const now = new Date()
  const thirtyDaysFromNow = new Date()
  thirtyDaysFromNow.setDate(now.getDate() + 30)
  return expiry >= now && expiry <= thirtyDaysFromNow
}

const formatExpiryDate = (expiryDate: string | null | undefined): string => {
  if (!expiryDate) return '-'
  return new Date(expiryDate).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getStatusChipProps = (status: DCInventoryItem['status']): { label: string; color: 'success' | 'warning' | 'error' } => {
  switch (status) {
    case 'in_stock':
      return { label: 'In Stock', color: 'success' }
    case 'low_stock':
      return { label: 'Low Stock', color: 'warning' }
    case 'depleted':
      return { label: 'Depleted', color: 'error' }
    default:
      return { label: status, color: 'success' }
  }
}

export default function DCInventoryPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: () => dcApi.getInventorySummary()
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory', page, rowsPerPage, search],
    queryFn: () => dcApi.getInventory({
      page: page + 1,
      limit: rowsPerPage,
      search: search || undefined
    })
  })

  const items = data?.items || []
  const pagination = data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 }

  const handleSearch = () => {
    setSearch(searchInput)
    setPage(0)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(items.map((item) => item.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    )
  }

  const isAllSelected = useMemo(
    () => items.length > 0 && selectedIds.length === items.length,
    [items.length, selectedIds.length]
  )

  const isSomeSelected = useMemo(
    () => selectedIds.length > 0 && selectedIds.length < items.length,
    [items.length, selectedIds.length]
  )

  const summaryCards = [
    {
      label: 'Total Items',
      value: summaryData?.total_items ?? 0,
      icon: <InventoryIcon color="primary" />,
      format: 'number' as const
    },
    {
      label: 'Unique Medicines',
      value: summaryData?.total_medicines ?? 0,
      icon: <MedicationIcon color="secondary" />,
      format: 'number' as const
    },
    {
      label: 'Total Qty',
      value: summaryData?.total_available_qty ?? 0,
      icon: <CategoryIcon color="info" />,
      format: 'number' as const
    },
    {
      label: 'Total Value',
      value: summaryData?.total_value ?? 0,
      icon: <AttachMoneyIcon color="success" />,
      format: 'currency' as const
    },
    {
      label: 'Expiring in 30d',
      value: summaryData?.expiring_30d ?? 0,
      icon: <WarningAmberIcon sx={{ color: 'warning.main' }} />,
      format: 'number' as const
    },
    {
      label: 'Expired',
      value: summaryData?.expired ?? 0,
      icon: <ErrorOutlineIcon color="error" />,
      format: 'number' as const
    }
  ]

  if (error) {
    return (
      <Alert severity="error">
        Failed to load inventory data: {(error as Error).message}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          DC Inventory
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<LocalShippingIcon />}
            onClick={() => navigate('/transfers/create', {
              state: selectedIds.length > 0 ? { inventoryIds: selectedIds } : undefined
            })}
          >
            {selectedIds.length > 0
              ? `Transfer ${selectedIds.length} Item${selectedIds.length > 1 ? 's' : ''} to Store`
              : 'Transfer to Store'}
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {summaryCards.map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  {card.icon}
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }} noWrap>
                    {card.label}
                  </Typography>
                </Box>
                {summaryLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="h5" fontWeight="bold">
                    {card.format === 'currency'
                      ? formatCurrency(card.value)
                      : (card.value as number).toLocaleString('en-IN')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by medicine name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Button variant="contained" fullWidth onClick={handleSearch}>
              Search
            </Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Chip
              label={`${pagination.total} items`}
              color="primary"
              variant="outlined"
              sx={{ width: '100%', justifyContent: 'center' }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Inventory Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No inventory items found
          </Typography>
          <Typography color="text.secondary">
            Confirm purchase bills to add items to inventory.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Medicine Name</TableCell>
                  <TableCell>Pack Size</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell align="right">Available Qty</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Transferred</TableCell>
                  <TableCell align="right">Rate (PTR)</TableCell>
                  <TableCell align="right">MRP</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const expired = isExpired(item.expiry_date)
                  const expiringSoon = isExpiringIn30Days(item.expiry_date)
                  const statusProps = getStatusChipProps(item.status)
                  const isSelected = selectedIds.includes(item.id)

                  return (
                    <TableRow
                      key={item.id}
                      hover
                      selected={isSelected}
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectRow(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.medicine_name || '-'}
                        </Typography>
                        {item.manufacturer_name && (
                          <Typography variant="caption" color="text.secondary">
                            {item.manufacturer_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.pack_size || '-'}</TableCell>
                      <TableCell>{item.batch_no || '-'}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: expired
                              ? 'error.main'
                              : expiringSoon
                                ? 'warning.main'
                                : 'text.primary',
                            fontWeight: expired || expiringSoon ? 600 : 400
                          }}
                        >
                          {formatExpiryDate(item.expiry_date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.quantity_available}</TableCell>
                      <TableCell align="right">{item.quantity_allocated}</TableCell>
                      <TableCell align="right">{item.quantity_transferred}</TableCell>
                      <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.mrp)}</TableCell>
                      <TableCell>{item.distributor_name || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusProps.label}
                          color={statusProps.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  )
}
